"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { ArrowUpRight, MapPin, Loader2 } from "lucide-react"
import { createClient } from "@/lib/supabase/client"
import { formatCity, formatState } from "@/lib/format-location"

type Follower = {
  id: string
  full_name: string | null
  avatar_url: string | null
  city: string | null
  state: string | null
  slug: string | null
}

export function FollowersModal({
  open,
  onOpenChange,
  barberProfileId,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  barberProfileId: string
}) {
  const [loading, setLoading] = useState(false)
  const [followers, setFollowers] = useState<Follower[]>([])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const supabase = createClient()
      const { data: rows } = await supabase
        .from("network_follows")
        .select("follower_id")
        .eq("following_id", barberProfileId)

      const ids: string[] = (rows || []).map((r: any) => r.follower_id)
      if (ids.length === 0) {
        if (!cancelled) {
          setFollowers([])
          setLoading(false)
        }
        return
      }

      const [{ data: profiles }, { data: barberProfiles }] = await Promise.all([
        supabase.from("profiles").select("id, full_name, avatar_url").in("id", ids),
        supabase.from("barber_profiles").select("id, slug, city, state").in("id", ids),
      ])

      const profileMap: Record<string, any> = {}
      for (const p of profiles || []) profileMap[p.id] = p
      const bpMap: Record<string, any> = {}
      for (const b of barberProfiles || []) bpMap[b.id] = b

      const merged: Follower[] = ids.map((id) => {
        const p = profileMap[id]
        const bp = bpMap[id]
        return {
          id,
          full_name: p?.full_name || null,
          avatar_url: p?.avatar_url || null,
          city: bp?.city || null,
          state: bp?.state || null,
          slug: bp?.slug || null,
        }
      })

      if (!cancelled) {
        setFollowers(merged)
        setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, barberProfileId])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 rounded-2xl overflow-hidden max-h-[80vh] flex flex-col">
        <div className="px-6 pt-6 pb-3">
          <DialogTitle className="text-lg font-semibold tracking-tight">Followers</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground -mt-0.5">
            People following this profile.
          </DialogDescription>
        </div>

        <div className="flex-1 overflow-y-auto px-3 pb-4 space-y-1">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : followers.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-12">No followers yet.</p>
          ) : (
            followers.map((f) => {
              const cityLabel = [formatCity(f.city || ""), formatState(f.state || "")].filter(Boolean).join(", ")
              const profileHref = f.slug ? `/barbers/${f.slug}` : null
              return (
                <div
                  key={f.id}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-muted/50 transition-colors"
                >
                  <Avatar className="w-10 h-10 border border-border flex-shrink-0">
                    <AvatarImage src={f.avatar_url || ""} />
                    <AvatarFallback className="bg-muted text-xs">{f.full_name?.charAt(0) || "?"}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm truncate leading-tight">{f.full_name || "Unknown"}</p>
                    {cityLabel && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{cityLabel}</span>
                      </p>
                    )}
                  </div>
                  {profileHref ? (
                    <Link
                      href={profileHref}
                      onClick={() => onOpenChange(false)}
                      className="flex-shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      aria-label={`View ${f.full_name || "profile"}`}
                      title="View profile"
                    >
                      <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
                    </Link>
                  ) : (
                    <span className="flex-shrink-0 w-9 h-9" aria-hidden />
                  )}
                </div>
              )
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
